import CDialBase from './Dials'
import { PlaceInNestedAkixiNameSpace } from '../utils/Utils'
import { EaseInOutQuad } from '../../EasingFunctions'

/**
 * This class draws a new semi-circle dial.
 * @param {object} pNode The DOM element to attach the dial canvas to.
 */
class CGradatedDial extends CDialBase {
    /**
     * Private method.
     *
     * Draws the dial on the canvas.
     *
     * @param {number} [flStopValue] Optional - The value at which the dial ends,
     * defaults to the current dial value.
     * @param {number} [flStartValue] Optional - The value at which the dial
     * starts, defaults to the stop value.
     * @param {number} [flFractionComplete=1.0] Optional - How far through the
     * animation we are, defaults to 100%.
     */
    protected Draw(
        flStopValue: number = this.m_flCurrentValue,
        flStartValue: number = flStopValue,
        flFractionComplete = 1.0
    ): void {
        const p2DContext = this.m_pCanvas.getContext('2d') as CanvasRenderingContext2D
        const flFillRatio = 0.75
        const iWidth = this.m_iWidth
        const iHeight = this.m_iHeight
        const flLineRatio = 0.25
        const flSize = Math.min(iWidth / 2, iHeight)
        let flRadius = flSize * flFillRatio
        const flProgressLineWidth = flRadius / 50
        let flFontSize = flRadius
        const flDotSize = flProgressLineWidth * 1.5

        /*
         * Draw the outer dial elements
         */
        p2DContext.fillStyle = this.m_sBackgroundColour
        p2DContext.fillRect(0, 0, this.m_iWidth, this.m_iHeight)

        let flOpacity = 0.4
        let flOpacityStep = 0.2
        if (this.m_bReverseGuageFace) {
            flOpacity = 1.0
            flOpacityStep = -0.2
        }

        const flGradatedArcWidth = flRadius * flLineRatio
        p2DContext.lineWidth = flGradatedArcWidth
        // Take into account the line width
        flRadius -= p2DContext.lineWidth / 2
        flRadius = Math.max(flRadius, 0) // Prevent an error on a zero sized canvas

        let flVerticalPosition = (0.5 + flFillRatio / 2) * iHeight
        if (iHeight > iWidth / 2) {
            // Centre the dial
            flVerticalPosition -= (flFillRatio * (iHeight - iWidth / 2)) / 2
        }

        p2DContext.lineCap = 'butt'
        p2DContext.strokeStyle = this.BuildForegroundWithOpacity(flOpacity)
        flOpacity += flOpacityStep
        p2DContext.fillStyle = this.m_sForegroundColour

        p2DContext.beginPath()
        p2DContext.arc(iWidth / 2, flVerticalPosition, flRadius, Math.PI, Math.PI * 1.25)
        p2DContext.stroke()

        p2DContext.strokeStyle = this.BuildForegroundWithOpacity(flOpacity)
        flOpacity += flOpacityStep
        p2DContext.fillStyle = this.m_sForegroundColour
        p2DContext.beginPath()
        p2DContext.arc(iWidth / 2, flVerticalPosition, flRadius, Math.PI * 1.25, Math.PI * 1.5)
        p2DContext.stroke()

        p2DContext.strokeStyle = this.BuildForegroundWithOpacity(flOpacity)
        flOpacity += flOpacityStep
        p2DContext.fillStyle = this.m_sForegroundColour
        p2DContext.beginPath()
        p2DContext.arc(iWidth / 2, flVerticalPosition, flRadius, Math.PI * 1.5, Math.PI * 1.75)
        p2DContext.stroke()

        p2DContext.strokeStyle = this.BuildForegroundWithOpacity(flOpacity)
        p2DContext.fillStyle = this.m_sForegroundColour
        p2DContext.beginPath()
        p2DContext.arc(iWidth / 2, flVerticalPosition, flRadius, Math.PI * 1.75, Math.PI * 2)
        p2DContext.stroke()

        /*
         * Draw the progress bar.
         */

        p2DContext.fillStyle = 'red'
        p2DContext.beginPath()
        p2DContext.arc(iWidth / 2, flVerticalPosition, flDotSize, 0, Math.PI * 2)
        p2DContext.fill()

        /*
         * Draw the centre text.
         */
        const flDisplayValue = EaseInOutQuad(flFractionComplete) * (flStopValue - flStartValue) + flStartValue
        //
        let flPhi = (flDisplayValue * Math.PI) / this.m_flRange
        if (this.m_bReverseDirection) {
            flPhi = Math.PI - flPhi
        }
        //
        const flXAxis = iWidth / 2 - flRadius * Math.cos(flPhi)
        const flYAxis = flVerticalPosition - flRadius * Math.sin(flPhi)
        p2DContext.strokeStyle = 'red'
        p2DContext.lineWidth = flProgressLineWidth
        p2DContext.lineCap = 'round'
        p2DContext.beginPath()
        p2DContext.moveTo(iWidth / 2, flVerticalPosition)
        p2DContext.lineTo(flXAxis, flYAxis)
        p2DContext.stroke()
        //
        const flTextPosition = flVerticalPosition - (flRadius - flGradatedArcWidth) / 2
        p2DContext.fillStyle = this.m_sForegroundColour
        p2DContext.font = `${flFontSize}px ${this.m_sFontFamily}`
        p2DContext.textAlign = 'center'
        p2DContext.textBaseline = 'middle'
        const pTextDimensions = p2DContext.measureText(this.m_sDisplayValue)
        const flWidthRatio = (flRadius * 1.3) / pTextDimensions.width
        const flHeightRatio = (flRadius * 0.7) / flFontSize
        const flRatio = Math.min(flHeightRatio, flWidthRatio)
        flFontSize *= flRatio
        p2DContext.font = `${flFontSize}px ${this.m_sFontFamily}`
        p2DContext.fillText(this.m_sDisplayValue, iWidth / 2, flTextPosition)
    }
}

export default CGradatedDial
PlaceInNestedAkixiNameSpace('Dials', 'CGradatedDial', CGradatedDial)
